import { useCallback, useRef, useEffect } from 'react';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

interface VirtualDPadProps {
  onDirectionChange: (directions: { up: boolean; down: boolean; left: boolean; right: boolean }) => void;
}

export const VirtualDPad = ({ onDirectionChange }: VirtualDPadProps) => {
  const directionsRef = useRef({ up: false, down: false, left: false, right: false });

  const updateDirection = useCallback((key: 'up' | 'down' | 'left' | 'right', pressed: boolean) => {
    directionsRef.current[key] = pressed;
    onDirectionChange({ ...directionsRef.current });
  }, [onDirectionChange]);

  const handleTouchStart = useCallback((key: 'up' | 'down' | 'left' | 'right') => (e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    updateDirection(key, true);
  }, [updateDirection]);

  const handleTouchEnd = useCallback((key: 'up' | 'down' | 'left' | 'right') => (e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    updateDirection(key, false);
  }, [updateDirection]);

  // Reset all directions when component unmounts or touch is cancelled
  useEffect(() => {
    return () => {
      onDirectionChange({ up: false, down: false, left: false, right: false });
    };
  }, [onDirectionChange]);

  const buttonClass = "w-12 h-12 flex items-center justify-center rounded-lg bg-primary/20 border border-primary/50 text-primary active:bg-primary/40 active:scale-95 transition-all touch-none select-none";

  return (
    <div className="flex flex-col items-center gap-1">
      {/* Up button */}
      <button
        className={buttonClass}
        onTouchStart={handleTouchStart('up')}
        onTouchEnd={handleTouchEnd('up')}
        onTouchCancel={handleTouchEnd('up')}
      >
        <ChevronUp size={28} strokeWidth={3} />
      </button>
      
      {/* Left, Down, Right row */}
      <div className="flex gap-1">
        <button
          className={buttonClass}
          onTouchStart={handleTouchStart('left')}
          onTouchEnd={handleTouchEnd('left')}
          onTouchCancel={handleTouchEnd('left')}
        >
          <ChevronLeft size={28} strokeWidth={3} />
        </button>
        
        <button
          className={buttonClass}
          onTouchStart={handleTouchStart('down')}
          onTouchEnd={handleTouchEnd('down')}
          onTouchCancel={handleTouchEnd('down')}
        >
          <ChevronDown size={28} strokeWidth={3} />
        </button>
        
        <button
          className={buttonClass}
          onTouchStart={handleTouchStart('right')}
          onTouchEnd={handleTouchEnd('right')}
          onTouchCancel={handleTouchEnd('right')}
        >
          <ChevronRight size={28} strokeWidth={3} />
        </button>
      </div>
    </div>
  );
};
